// assets/auto-discount-price.js
// One-call auto-discount display:
//  • Call Storefront GraphQL ONCE for the currently selected (or first) variant at qty=1
//  • Compute discountPercent = (base - discounted) / base
//  • Apply that percent to PDP price, all QOL rows, and the Total Cost
//
// Assumptions per request:
//  - All variants have the SAME base price
//  - The automatic discount applies uniformly to ALL variants
//  - We only need qty=1 sampling (no tier/threshold math)
//
// Vanilla JS. No jQuery.

(() => {
  const API_VERSION = '2025-07';
  const META_TOKEN_SELECTOR = 'meta[name="storefront-access-token"]';
  const SALE_COLOR = '#FF7A8A';
  const GREY_COLOR = '#9aa0a6';

  // ---------- small utils ----------
  const formatMoney = (cents, moneyFormat) => {
    try {
      cents = Math.round(Number(cents) || 0);
      const fmt = moneyFormat || '${{amount}}';
      const noDec = fmt.includes('{{amount_no_decimals}}') || fmt.includes('{{amount_no_decimals_with_currency}}');
      const value = noDec ? (cents / 100).toFixed(0) : (cents / 100).toFixed(2);
      const [whole, dec = ''] = value.split('.');
      const wholeWithSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt
        .replace('{{amount_no_decimals_with_currency}}', wholeWithSep)
        .replace('{{amount_with_space}}', `${wholeWithSep}${dec ? '.' + dec : ''}`)
        .replace('{{amount_with_currency}}', `${wholeWithSep}${dec ? '.' + dec : ''}`)
        .replace('{{amount_no_decimals}}', wholeWithSep)
        .replace('{{amount}}', `${wholeWithSep}${dec ? '.' + dec : ''}`);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };
  const gidForVariant = (id) => `gid://shopify/ProductVariant/${id}`;
  const parseGID = (gid) => {
    const m = /ProductVariant\/(\d+)/.exec(gid || '');
    return m ? Number(m[1]) : null;
  };
  const clamp01 = (n) => Math.max(0, Math.min(1, n));

  function injectCSSOnce() {
    if (document.getElementById('vnc-adp-style')) return;
    const css = `
      /* PDP */
      .vnc-force-sale .price__sale{display:inline-flex!important;gap:.45rem;align-items:baseline;}
      .vnc-force-sale .price__regular .price-item--regular{color:${GREY_COLOR}!important;text-decoration:line-through;}
      .vnc-force-sale .price__sale .price-item--sale{color:${SALE_COLOR}!important;font-weight:600;line-height:1;}

      /* QOL */
      .vnc-qol-old{color:${GREY_COLOR};text-decoration:line-through;margin-right:.4rem;}
      .vnc-qol-new{color:${SALE_COLOR};font-weight:600;}

      /* Total */
      .totals__subtotal-value .vnc-total-old{color:${GREY_COLOR};text-decoration:line-through;margin-right:.45rem;}
      .totals__subtotal-value .vnc-total-new{color:${SALE_COLOR};font-weight:700;}
    `;
    const el = document.createElement('style');
    el.id = 'vnc-adp-style';
    el.textContent = css;
    document.head.appendChild(el);
  }

  // ---------- Storefront: one lightweight call ----------
  async function oneProbePercent(variantId, token) {
    // Create a cart with ONE line, quantity 1. Read unit base and unit discounted.
    const endpoint = `/api/${API_VERSION}/graphql.json`;
    const query = `
      mutation vncCartCreate($lines:[CartLineInput!]!){
        cartCreate(input:{lines:$lines}){
          cart{
            lines(first:1){
              edges{
                node{
                  quantity
                  merchandise{__typename ... on ProductVariant{id}}
                  cost{amountPerQuantity{amount}}
                  discountAllocations{discountedAmount{amount}}
                }
              }
            }
          }
          userErrors{message}
        }
      }`;
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {'Content-Type':'application/json','X-Shopify-Storefront-Access-Token': token},
      credentials: 'same-origin',
      body: JSON.stringify({ query, variables: { lines: [{ merchandiseId: gidForVariant(variantId), quantity: 1 }] } })
    });

    // If throttled or blocked, just return 0% (graceful)
    if (!res.ok) return 0;

    const json = await res.json();
    if (json?.errors?.[0]?.extensions?.code === 'THROTTLED') return 0;
    if (json?.data?.cartCreate?.userErrors?.length) return 0;

    const edges = json?.data?.cartCreate?.cart?.lines?.edges || [];
    const node = edges[0]?.node;
    if (!node) return 0;

    const base = Math.round(Number(node?.cost?.amountPerQuantity?.amount || 0) * 100);
    const lineDisc = Math.round((node?.discountAllocations || [])
      .reduce((s, d) => s + Number(d?.discountedAmount?.amount || 0), 0) * 100);
    const unitDisc = Math.max(0, base - lineDisc); // per-unit at qty=1
    if (base <= 0) return 0;

    const pct = clamp01((base - unitDisc) / base);
    return pct; // e.g., 0.10 for 10%
  }

  // ---------- PDP wiring ----------
  function getCurrentVariantId(sectionId) {
    const root = document.getElementById(`ProductInfo-${sectionId}`) || document;
    const input = root.querySelector('form [name="id"]');
    return input ? Number(input.value) : null;
  }
  function applyPDP(sectionId, moneyFmt, baseCents, pct) {
    const priceRoot = document.querySelector(`#price-${sectionId}`) || document.getElementById(`price-${sectionId}`);
    if (!priceRoot) return;
    const saleCents = Math.max(0, Math.round(baseCents * (1 - pct)));
    const hasDiscount = pct > 0 && saleCents < baseCents;

    const regularSpan = priceRoot.querySelector('.price__regular .price-item--regular') ||
                        priceRoot.querySelector('.price-item--regular');
    let saleWrap = priceRoot.querySelector('.price__sale');
    let saleSpan = priceRoot.querySelector('.price__sale .price-item--sale');

    if (!hasDiscount) { priceRoot.classList.remove('vnc-force-sale'); return; }

    if (!saleWrap) {
      saleWrap = document.createElement('div');
      saleWrap.className = 'price__sale';
      const reg = priceRoot.querySelector('.price__regular') || priceRoot.querySelector('.price__container') || priceRoot;
      reg && reg.parentNode.insertBefore(saleWrap, reg.nextSibling);
    }
    if (!saleSpan) {
      saleSpan = document.createElement('span');
      saleSpan.className = 'price-item price-item--sale';
      saleWrap.appendChild(saleSpan);
    }

    if (regularSpan) {
      regularSpan.textContent = formatMoney(baseCents, moneyFmt);
      regularSpan.style.color = GREY_COLOR;
      regularSpan.style.textDecoration = 'line-through';
    }
    saleSpan.textContent = formatMoney(saleCents, moneyFmt);
    saleSpan.style.color = SALE_COLOR;
    saleSpan.style.fontWeight = '600';
    priceRoot.classList.add('vnc-force-sale');
  }

  // ---------- QOL wiring ----------
  function getQOLRows() {
    const table = document.querySelector('.quick-order-list__table');
    if (!table) return [];
    return Array.from(table.querySelectorAll('tr.variant-row'));
  }
  function readQOLRow(row) {
    const input = row.querySelector('input.quick-order-list__quantity-input');
    if (!input) return null;
    const baseCents = Number(input.getAttribute('data-variant-price') || 0);
    const qty = Math.max(0, Number(input.value || 0));
    const priceCell = row.querySelector('td.small-hide.medium-hide') || row.querySelector('td:last-child');
    return { baseCents, qty, priceCell, input };
  }
  function renderQOLCell(priceCell, moneyFmt, baseCents, pct, qty) {
    if (!priceCell) return;
    const saleUnit = Math.max(0, Math.round(baseCents * (1 - pct)));
    const baseTotal = baseCents * Math.max(1, qty);
    const saleTotal = saleUnit * Math.max(1, qty);
    if (pct <= 0 || saleTotal >= baseTotal) {
      priceCell.textContent = formatMoney(baseTotal, moneyFmt);
      return;
    }
    priceCell.innerHTML = `
      <span class="vnc-qol-old">${formatMoney(baseTotal, moneyFmt)}</span>
      <span class="vnc-qol-new">${formatMoney(saleTotal, moneyFmt)}</span>
    `;
  }
  function renderTotal(moneyFmt, rows, pct) {
    const el = document.getElementById('total-price');
    if (!el) return;
    let baseTotal = 0, saleTotal = 0;
    for (const r of rows) {
      if (!r.qty) continue;
      const saleUnit = Math.max(0, Math.round(r.baseCents * (1 - pct)));
      baseTotal += r.baseCents * r.qty;
      saleTotal += saleUnit * r.qty;
    }
    if (pct <= 0 || saleTotal >= baseTotal) {
      el.textContent = formatMoney(baseTotal, moneyFmt);
      return;
    }
    el.innerHTML = `
      <span class="vnc-total-old">${formatMoney(baseTotal, moneyFmt)}</span>
      <span class="vnc-total-new">${formatMoney(saleTotal, moneyFmt)}</span>
    `;
  }

  // ---------- bootstrap per PDP section ----------
  function bootstrapSection(sectionId) {
    const cfgEl = document.getElementById(`vnc-adp-config-${sectionId}`);
    if (!cfgEl) return;

    let cfg; try { cfg = JSON.parse(cfgEl.textContent); } catch { return; }
    const moneyFmt = cfg.moneyFormat || '${{amount}}';
    const variantMap = new Map((cfg.variantMap || []).map(v => [Number(v.id), v]));
    const token = document.querySelector(META_TOKEN_SELECTOR)?.content || '';
    const useSF = !!cfg.useStorefrontApi && !!token;

    injectCSSOnce();

    let discountPct = 0; // cache for the page

    async function initPercent() {
      if (!useSF) { discountPct = 0; return; }
      // Representative variant: current or first in map
      const curId = (getCurrentVariantId(cfg.sectionId)) || (variantMap.keys().next().value);
      const rep = variantMap.get(Number(curId));
      if (!rep) { discountPct = 0; return; }

      // ONE probe call at qty=1
      try {
        const pct = await oneProbePercent(Number(curId), token);
        discountPct = clamp01(pct);
      } catch {
        discountPct = 0;
      }
    }

    function updatePDP() {
      const vid = getCurrentVariantId(cfg.sectionId);
      if (!vid) return;
      const v = variantMap.get(Number(vid));
      if (!v) return;
      const base = Number(v.price_cents) || 0;
      applyPDP(cfg.sectionId, moneyFmt, base, discountPct);
    }

    function updateQOL() {
      const rowsEls = getQOLRows();
      if (!rowsEls.length) return;
      const rows = rowsEls.map(readQOLRow).filter(Boolean);
      for (const r of rows) renderQOLCell(r.priceCell, moneyFmt, r.baseCents, discountPct, r.qty || 1);
      renderTotal(moneyFmt, rows, discountPct);
    }

    (async () => {
      await initPercent(); // one API call
      updatePDP();
      updateQOL();
    })();

    // PDP variant change: no new API call—just recalc from cached percent
    const root = document.getElementById(`ProductInfo-${cfg.sectionId}`) || document;
    root.addEventListener('change', (e) => {
      if (e.target && (e.target.name === 'id' || e.target.closest('variant-radios') || e.target.closest('variant-selects'))) {
        updatePDP();
      }
      if (e.target && e.target.classList.contains('quick-order-list__quantity-input')) {
        updateQOL();
      }
    });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quantity-plus, .quantity-minus, .gc-quantity');
      if (!btn) return;
      // Let theme adjust the input, then recompute
      setTimeout(updateQOL, 0);
    });

    ['variant:change','variantChange','product:variant-change'].forEach(ev => document.addEventListener(ev, updatePDP));
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[id^="vnc-adp-config-"]').forEach(el => {
      const sid = el.id.replace('vnc-adp-config-', '');
      bootstrapSection(sid);
    });
  });
})();
